import { useState, useEffect, useRef } from 'react';
import { prepareLicenceFile } from '../utils/compressImage';

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

// One side of a driver licence. The file lives in a private server folder, so the preview is
// fetched with the login token as a blob rather than linked by URL.
//   meta        { uploaded, ext, size, uploadedAt }
//   fetchBlob   () => Promise<Blob>             (loads the saved file for preview)
//   onUpload    (File) => Promise               (omit for read-only, e.g. the manager view)
//   onRemove    () => Promise
export default function LicenceUpload({ label, meta, fetchBlob, onUpload, onRemove, error }) {
  const inputRef = useRef(null);
  const [busy,    setBusy]    = useState(false);
  const [message, setMessage] = useState(null);
  const [url,     setUrl]     = useState(null);

  const readOnly = !onUpload;
  const stamp    = meta?.uploadedAt;

  useEffect(() => {
    if (!meta?.uploaded) { setUrl(null); return undefined; }
    let objectUrl; let cancelled = false;
    fetchBlob()
      .then(blob => { if (!cancelled) { objectUrl = URL.createObjectURL(blob); setUrl(objectUrl); } })
      .catch(() => { if (!cancelled) setUrl(null); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [meta?.uploaded, stamp]);

  const choose = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true); setMessage(null);
    try {
      await onUpload(await prepareLicenceFile(file));
    } catch (err) {
      setMessage(err.response?.data?.message || err.message || 'Upload failed. Please try again.');
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true); setMessage(null);
    try { await onRemove(); }
    catch (err) { setMessage(err.response?.data?.message || 'Could not remove the file'); }
    finally { setBusy(false); }
  };

  const btn = { padding:'7px 12px', borderRadius:'var(--radius)', border:'1px solid var(--border-md)', background:'var(--surface)', color:'var(--text)', fontSize:'12px', cursor:'pointer', fontFamily:'inherit' };

  return (
    <div style={{ marginBottom:'14px' }}>
      <div style={{ fontSize:'11px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'.5px', color:'var(--muted)', marginBottom:'6px' }}>{label}</div>

      <div style={{ border:`1.5px ${meta?.uploaded ? 'solid' : 'dashed'} ${error ? 'var(--red)' : 'var(--border-md)'}`, borderRadius:'var(--radius)', background:'var(--bg)', padding:'12px' }}>
        {busy ? (
          <div style={{ textAlign:'center', color:'var(--muted)', fontSize:'13px', padding:'14px 0' }}>
            <i className="ti ti-loader" style={{ fontSize:'20px', color:'var(--blue)', display:'block', marginBottom:'6px' }} aria-hidden="true"/>Uploading…
          </div>
        ) : meta?.uploaded ? (
          <div style={{ display:'flex', gap:'12px', alignItems:'center', flexWrap:'wrap' }}>
            {meta.ext === 'pdf' ? (
              <a href={url || '#'} target="_blank" rel="noreferrer"
                style={{ width:'96px', height:'72px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'4px', border:'1px solid var(--border)', borderRadius:'var(--radius)', background:'var(--surface)', color:'var(--red)', textDecoration:'none', fontSize:'11px', fontWeight:'600' }}>
                <i className="ti ti-file-type-pdf" style={{ fontSize:'26px' }} aria-hidden="true"/>Open PDF
              </a>
            ) : url ? (
              <a href={url} target="_blank" rel="noreferrer" title="Open full size">
                <img src={url} alt={label} style={{ width:'96px', height:'72px', objectFit:'cover', borderRadius:'var(--radius)', border:'1px solid var(--border)', display:'block' }}/>
              </a>
            ) : (
              <div style={{ width:'96px', height:'72px', borderRadius:'var(--radius)', border:'1px solid var(--border)', background:'var(--surface)' }}/>
            )}
            <div style={{ flex:1, minWidth:'120px' }}>
              <div style={{ fontSize:'13px', color:'var(--green)', fontWeight:'500', display:'flex', alignItems:'center', gap:'6px' }}>
                <i className="ti ti-circle-check" aria-hidden="true"/> Uploaded
              </div>
              <div style={{ fontSize:'11px', color:'var(--muted)', marginTop:'2px' }}>{meta.ext?.toUpperCase()} · {fmtSize(meta.size || 0)}</div>
              {!readOnly && (
                <div style={{ display:'flex', gap:'6px', marginTop:'8px' }}>
                  <button type="button" onClick={()=>inputRef.current?.click()} style={btn}>Replace</button>
                  {onRemove && <button type="button" onClick={remove} style={{ ...btn, color:'var(--red)' }}>Remove</button>}
                </div>
              )}
            </div>
          </div>
        ) : readOnly ? (
          <div style={{ fontSize:'13px', color:'var(--hint)', padding:'6px 0' }}>Not uploaded</div>
        ) : (
          <button type="button" onClick={()=>inputRef.current?.click()}
            style={{ width:'100%', background:'none', border:'none', cursor:'pointer', padding:'14px 0', color:'var(--blue)', fontFamily:'inherit', fontSize:'13px', fontWeight:'500' }}>
            <i className="ti ti-camera-plus" style={{ fontSize:'24px', display:'block', marginBottom:'6px' }} aria-hidden="true"/>
            Take a photo or choose a file
            <div style={{ color:'var(--hint)', fontSize:'11px', fontWeight:'400', marginTop:'4px' }}>JPG, PNG or PDF · up to 5 MB</div>
          </button>
        )}
        {!readOnly && <input ref={inputRef} type="file" accept="image/*,application/pdf" onChange={choose} style={{ display:'none' }}/>}
      </div>

      {(message || (error && typeof error === 'string')) && (
        <div role="alert" style={{ color:'var(--red)', fontSize:'12px', marginTop:'5px' }}>{message || error}</div>
      )}
    </div>
  );
}
